import React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {DialogTitle, HTMLDialog} from '@priority-software/priority-style-react';
import {useEventListener} from '../../../../../../hooks/useEventListener';
import {keys} from '../../../../../../constants/KeyCodes';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import styles from './Modal.module.scss';

export default function useModal(): [
    JSX.Element | null,
    (title: string, showModal: (onClose: () => void) => JSX.Element) => void
] {
    const [modalContent, setModalContent] = useState<null | {
        closeOnClickOutside: boolean;
        content: JSX.Element;
        title: string;
    }>(null);
    const [editor] = useLexicalComposerContext();

    const onClose = useCallback(() => {
        setModalContent(null);
    }, []);

    useEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === keys.ESC && !!modalContent) {
            e?.stopImmediatePropagation();
            onClose();
            editor.focus();
        }
    });

    const modal = useMemo(() => {
        if (modalContent === null) return null;

        const {title, content} = modalContent;

        return (
            <HTMLDialog
                width={520}
                componentClasses={styles.component}
                size="small"
                toolbarContent={<DialogTitle skin="h4" title={title} />}
                onClose={onClose}
                isOpen
                content={content}
                escClosable={false}
            />
        );
    }, [modalContent, onClose]);

    const showModal = useCallback(
        (
            title: string,
            // eslint-disable-next-line no-shadow
            getContent: (onClose: () => void) => JSX.Element,
            closeOnClickOutside = false
        ) => {
            setModalContent({
                closeOnClickOutside,
                content: getContent(onClose),
                title
            });
        },
        [onClose]
    );

    return [modal, showModal];
}
